/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CommonModule } from '@angular/common';
import { CSP_NONCE, NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { RouterModule } from '@angular/router';
import { NgxsModule } from '@ngxs/store';

import { environment } from '../../environments/environment';
import { CORE_PLAYLIST_DATA_SERVICE } from '../page-modules/playlist/services/create/core/data.service';
import { RestClientService } from '../shared/services/rest-client.service';
import * as SharedFactoryService from '../shared/shared-service-factory';
import { SharedModule } from '../shared/shared.module';
import { AccountVerificationComponent } from './components/account-verification/account-verification.component';
import { EmailRecoveryComponent } from './components/email-recovery/email-recovery.component';
import { FolioAccountVerificationComponent } from './components/folio-account-verification/folio-account-verification.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { IframeLogoutComponent } from './components/iframe-logout/iframe-logout.component';
import { LoginFormComponent } from './components/login-form/login-form.component';
import { LtiLaunchErrorComponent } from './components/lti-launch-error/lti-launch-error.component';
import { LtiLaunchComponent } from './components/lti-launch/lti-launch.component';
import { LtiRegistrationErrorComponent } from './components/lti-registration-error/lti-registration-error.component';
// eslint-disable-next-line max-len
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { TranslocoService } from '@ngneat/transloco';
import {
  RECAPTCHA_LOADER_OPTIONS,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaFormsModule,
  RecaptchaLoaderOptions,
  RecaptchaModule,
  RecaptchaV3Module,
} from 'ng-recaptcha-2';
import { PtlEditorModule } from '../editor/editor.module';
import { FolioPublicMomentContentComponent } from '../page-modules/folio/components/view/public-moment/public-moment-content.component';
import { TranslocoRootModule } from '../transloco/transloco-root.module';
import { AlumniLoginComponent } from './components/alumni-login/alumni-login.component';
import { ClassofAuthComponent } from './components/classof-auth/classof-auth.component';
import { ClassOfLoginComponent } from './components/classof-auth/classof-login/classof-login.component';
import { ClassofSignupComponent } from './components/classof-auth/classof-singup/classof-signup.component';
import { InvitationJoinComponent } from './components/invitation-join/invitation-join.component';
import { LtiDeeplinkingComponent } from './components/lti-deeplinking/lti-deeplinking.component';
import { LtiPlaylistEditComponent } from './components/lti-playlist-edit/lti-playlist-edit.component';
import { PasswordConfirmationAndRecoveryComponent } from './components/password-confirmation-and-recovery/password-confirmation-and-recovery.component';
import { PrearrivalsLoginFormComponent } from './components/prearrivals-login-form/prearrivals-login-form.component';
import { RecoveryEmailVerificationComponent } from './components/recovery-email-verification/recovery-email-verification.component';
import { SamlSpErrorComponent } from './components/saml-sp/saml-sp-error/saml-sp-error.component';
import { SamlSPLaunchComponent } from './components/saml-sp/saml-sp.component';
import { SignupDetailsFormComponent } from './components/signup-form/details-form/signup-details-form.component';
import { SignupFormComponent } from './components/signup-form/signup-form.component';
import { UnverifiedEmailComponent } from './components/unverified-email/unverified-email.component';
import { VerificationTemplateComponent } from './components/verification-template/verification-template.component';
import { VerifyEmailComponent } from './components/verify-email/verify-email.component';
import { AccessTokenService } from './services/access-token.service';
import { ApiBasicAuthAuthService } from './services/api-basic-auth.service';
import { AUTH_SERVICE } from './services/basic-auth.service';
import { LTI_AUTH_SERVICE, LtiAuthService } from './services/lti-auth.service';
import { MockAuthService } from './services/mock-basic-auth.service';
import { UserAuthState } from './store/user-auth.state';

@NgModule({
  declarations: [
    LoginFormComponent,
    SignupDetailsFormComponent,
    ClassofAuthComponent,
    ClassOfLoginComponent,
    ClassofSignupComponent,
    AlumniLoginComponent,
    PrearrivalsLoginFormComponent,
    SignupFormComponent,
    AccountVerificationComponent,
    SamlSPLaunchComponent,
    SamlSpErrorComponent,
    LtiLaunchComponent,
    LtiDeeplinkingComponent,
    LtiPlaylistEditComponent,
    LtiRegistrationErrorComponent,
    LtiLaunchErrorComponent,
    IframeLogoutComponent,
    PasswordConfirmationAndRecoveryComponent,
    ForgotPasswordComponent,
    EmailRecoveryComponent,
    RecoveryEmailVerificationComponent,
    FolioAccountVerificationComponent,
    UnverifiedEmailComponent,
    VerifyEmailComponent,
    VerificationTemplateComponent,
    FolioPublicMomentContentComponent,
    InvitationJoinComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    NgxsModule.forFeature([UserAuthState]),
    TranslocoRootModule,
    MatInputModule,
    MatCheckboxModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatCardModule,
    SharedModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatRadioModule,
    PtlEditorModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    RecaptchaV3Module,
  ],
  providers: [
    AccessTokenService,
    { provide: AUTH_SERVICE, useClass: environment.disableAuth ? MockAuthService : ApiBasicAuthAuthService },
    { provide: LTI_AUTH_SERVICE, useClass: LtiAuthService },
    {
      provide: CORE_PLAYLIST_DATA_SERVICE,
      useFactory: SharedFactoryService.playlistServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.reCaptchaSiteKeyV3 },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useValue: (nonce: string): RecaptchaLoaderOptions => ({
        onBeforeLoad: (_url) => ({
          url: _url,
          nonce: nonce,
        }),
      }),
      deps: [CSP_NONCE],
    },
  ],
  exports: [LoginFormComponent, SignupFormComponent],
})
export class UserAuthModule {}
